import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
  Skeleton,
  Grid,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import { sub } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dashboardAPI } from '../services/dashboard-api';
import DateRangeSelector from './dashboard/DateRangeSelector';
import HeroKPIs from './dashboard/HeroKPIs';
import AcquisitionBreakdown from './dashboard/AcquisitionBreakdown';
import TrafficPerformance from './dashboard/TrafficPerformance';
import TrendCharts from './dashboard/TrendCharts';
import PartnerPerformanceTable from './dashboard/PartnerPerformanceTable';
import ClicksOrdersTab from './dashboard/ClicksOrdersTab';
import BrandsTab from './dashboard/buyer-insights/BrandsTab';
import ProductsTab from './dashboard/buyer-insights/ProductsTab';
import { track } from '../utils/analytics';


const DashboardHome = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const [startDate, setStartDate] = useState(() => sub(new Date(), { days: 30 }));
  const [endDate, setEndDate] = useState(() => new Date());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [myRetailers, setMyRetailers] = useState([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState('');  // '' = all
  const [trafficByPartner, setTrafficByPartner] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [overdueCount, setOverdueCount] = useState(0);
  const tabViewTracked = useRef(false);
  // A link from Brands opens Products preset ({view, group, brand, filters}); the key remounts it.
  const [productsLink, setProductsLink] = useState({ key: 0, initial: {} });
  // The filters both tabs share (window, Brand, Category, Product live date, Sale) travel with the
  // user: the mounted tab reports them here and the other tab is seeded with them (spec §4.3).
  const [biFilters, setBiFilters] = useState({});

  const setTab = (t, source = 'tab') => {
    setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true });
    track('Retailer Dashboard Tab Viewed', { tab: t, source });
  };

  const openProducts = (initial) => {
    setProductsLink((prev) => ({ key: prev.key + 1, initial }));
    setTab('products', 'brands_link');
  };

  // A ?tab= deep link counts as a view too (once; StrictMode double-runs effects).
  useEffect(() => {
    if (!tabViewTracked.current && searchParams.get('tab')) {
      tabViewTracked.current = true;
      track('Retailer Dashboard Tab Viewed', { tab: activeTab, source: 'url' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch retailer group members on mount
  useEffect(() => {
    const loadRetailers = async () => {
      try {
        const response = await dashboardAPI.getMyRetailers();
        if (response.success && response.data.length > 1) {
          setMyRetailers(response.data);
        }
      } catch (err) {
        // Non-critical — just don't show dropdown
      }
    };
    loadRetailers();
  }, []);

  // Fetch overdue invoice count
  useEffect(() => {
    const loadOverdueCount = async () => {
      try {
        const response = await dashboardAPI.getOverdueInvoiceCount();
        if (response.success) {
          setOverdueCount(response.data.overdue_count);
        }
      } catch (err) {
        // Non-critical — just don't show banner
      }
    };
    loadOverdueCount();
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await dashboardAPI.getDashboardSummary({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        my_retailer_id: selectedRetailerId || undefined,
      });
      if (response.success) {
        setSummary(response.data);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedRetailerId]);

  const loadTrafficStats = useCallback(async () => {
    try {
      setTrafficLoading(true);
      const response = await dashboardAPI.getTrafficStats({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        my_retailer_id: selectedRetailerId || undefined,
      });
      if (response.success && response.data) {
        setTrafficByPartner(response.data.per_partner);
      }
    } catch {
      // Non-critical — partner table degrades to legacy columns
    } finally {
      setTrafficLoading(false);
    }
  }, [startDate, endDate, selectedRetailerId]);

  useEffect(() => {
    loadDashboardData();
    loadTrafficStats();
  }, [loadDashboardData, loadTrafficStats]);

  const handleDateChange = (newStart, newEnd) => {
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Handoff Performance
        </Typography>
        <Box mb={3}>
          <Skeleton variant="rectangular" height={40} width={500} sx={{ borderRadius: 1 }} />
        </Box>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5].map((item) => (
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={item}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="80%" height={40} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box mt={3}>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
        </Box>
        <Box mt={3}>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
        Handoff Performance
      </Typography>

      {overdueCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have {overdueCount} overdue invoice{overdueCount !== 1 ? 's' : ''}.{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/invoices?status=overdue')}
            sx={{ fontWeight: 600 }}
          >
            View invoices
          </Link>
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Overview" value="overview" />
        <Tab label="Clicks & orders" value="clicks" />
        <Tab label="Trends" value="trends" />
        <Tab label="Partners" value="partners" />
        <Tab label="Brands" value="brands" />
        <Tab label="Products" value="products" />
      </Tabs>

      {/* Filter bar — shown for Overview and Partners, not Trends (own date selector) nor Brands / Products (own filter card) */}
      {!['trends', 'brands', 'products'].includes(activeTab) && (
        <Paper
          variant="outlined"
          sx={{ px: 2, py: 1.5, mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 1.5 }, flexWrap: 'wrap' }}
        >
          {myRetailers.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={selectedRetailerId}
                onChange={(e) => setSelectedRetailerId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">All stores</MenuItem>
                {myRetailers.map((r) => (
                  <MenuItem key={r.retailer_id} value={r.retailer_id}>
                    {r.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
          />
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Brands and Products tabs (Buyer Insights) */}
      {activeTab === 'brands' && (
        <BrandsTab
          myRetailers={myRetailers}
          myRetailerId={selectedRetailerId}
          onRetailerChange={setSelectedRetailerId}
          onOpenProducts={openProducts}
          initial={biFilters}
          onFiltersChange={setBiFilters}
        />
      )}
      {activeTab === 'products' && (
        <ProductsTab
          key={productsLink.key}
          myRetailers={myRetailers}
          myRetailerId={selectedRetailerId}
          onRetailerChange={setSelectedRetailerId}
          initial={{ ...biFilters, ...productsLink.initial }}
          onFiltersChange={setBiFilters}
        />
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          <Box mb={3}>
            <HeroKPIs data={summary} />
          </Box>
          <Box mb={3}>
            <AcquisitionBreakdown data={summary} />
          </Box>
          <Box mb={3}>
            <TrafficPerformance
              startDate={startDate}
              endDate={endDate}
              myRetailerId={selectedRetailerId}
            />
          </Box>
        </>
      )}

      {/* Clicks & orders Tab — flows, click balance, sale filter impact */}
      {activeTab === 'clicks' && (
        <ClicksOrdersTab
          startDate={startDate}
          endDate={endDate}
          myRetailerId={selectedRetailerId}
        />
      )}

      {/* Trends Tab — TrendCharts owns its own date state */}
      {activeTab === 'trends' && (
        <TrendCharts myRetailerId={selectedRetailerId} />
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && trafficLoading && (
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
      )}
      {activeTab === 'partners' && !trafficLoading && (
        <PartnerPerformanceTable
          partners={summary?.partner_performance}
          trafficByPartner={trafficByPartner}
          startDate={startDate}
          endDate={endDate}
          myRetailerId={selectedRetailerId}
        />
      )}
    </Box>
  );
};

export default DashboardHome;
